import { useCallback, useState } from 'react';
import { deleteClient } from '@/lib/api/clients';
import { invalidateClientDetailsCache } from '@/hooks/useClientDetails';
import { getDeleteClientErrorCopy, isPendingDeletionClient } from '@/utils/client-account-deletion';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';

type DeleteClientTarget = {
  id: string;
  name: string;
  isPendingDeletion: boolean;
};

type RequestDeleteClientInput = {
  id: string;
  name?: string | null;
  status?: string | null;
  isPendingDeletion?: boolean;
};

export function useDeleteClient() {
  const [target, setTarget] = useState<DeleteClientTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const requestDeleteClient = useCallback((input: RequestDeleteClientInput) => {
    const id = input.id.trim();
    if (!id) return;

    setTarget({
      id,
      name: input.name?.trim() || 'this client',
      isPendingDeletion: input.isPendingDeletion ?? isPendingDeletionClient({
        status: input.status,
      }),
    });
  }, []);

  const closeDeleteClient = useCallback(() => {
    if (isDeleting) return;
    setTarget(null);
  }, [isDeleting]);

  const confirmDeleteClient = useCallback(async (): Promise<boolean> => {
    if (!target || isDeleting) return false;

    setIsDeleting(true);
    try {
      const response = await deleteClient(target.id);
      invalidateClientDetailsCache(target.id);
      showSuccessToast(
        'Client deleted',
        response?.message || `${target.name} was permanently deleted.`
      );
      setTarget(null);
      return true;
    } catch (error) {
      const copy = getDeleteClientErrorCopy(error);
      showErrorToast(error, copy.title);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, target]);

  return {
    deleteTarget: target,
    isDeletingClient: isDeleting,
    isDeleteDialogOpen: Boolean(target),
    requestDeleteClient,
    closeDeleteClient,
    confirmDeleteClient,
  };
}
